import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Save, Loader2, ChevronLeft, ChevronRight, Plus, X, Edit2, Trash2, RotateCcw, CheckCircle2, Timer, Sparkles } from 'lucide-react';
import { 
  setAvailability, 
  getConfiguredSlots, 
  getWeeklySchedule, 
  setWeeklySchedule,
  deleteAvailabilityOverride,
  hasDateOverride
} from '../firebase/availability';
import { formatDisplayDate, isToday, sortTimeSlots } from '../utils/dateHelpers';
import { FeatureGuide, HelpTooltip, InfoBanner } from './HelpTooltip';

const DAYS_OF_WEEK = [
  { id: 'monday', label: 'Monday', short: 'Mon', icon: '📅' },
  { id: 'tuesday', label: 'Tuesday', short: 'Tue', icon: '📅' },
  { id: 'wednesday', label: 'Wednesday', short: 'Wed', icon: '📅' },
  { id: 'thursday', label: 'Thursday', short: 'Thu', icon: '📅' },
  { id: 'friday', label: 'Friday', short: 'Fri', icon: '📅' },
  { id: 'saturday', label: 'Saturday', short: 'Sat', icon: '📅' },
  { id: 'sunday', label: 'Sunday', short: 'Sun', icon: '📅' }
];

// Generate time slots from a time range (24-hour format)
const generateSlotsFromRange = (startTime, endTime, intervalMinutes = 30) => {
  const slots = [];
  
  const parseTime = (timeStr) => {
    // Expects format "HH:MM" (24-hour)
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes; // Convert to minutes since midnight
  };
  
  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };
  
  const startMinutes = parseTime(startTime);
  const endMinutes = parseTime(endTime);
  
  if (startMinutes >= endMinutes) return [];
  
  for (let minutes = startMinutes; minutes < endMinutes; minutes += intervalMinutes) {
    slots.push(formatTime(minutes));
  }
  
  return slots;
};

// Convert time ranges to slots for availability
const convertRangesToSlots = (ranges) => {
  if (!ranges || !Array.isArray(ranges)) return [];
  
  const allSlots = [];
  ranges.forEach(range => {
    if (range.start && range.end) {
      const slots = generateSlotsFromRange(range.start, range.end, range.interval || 30);
      allSlots.push(...slots);
    }
  });
  
  // Remove duplicates and sort
  return sortTimeSlots([...new Set(allSlots)]);
};

export function AdminAvailabilityTab({ 
  selectedDate, 
  setSelectedDate, 
  defaultTimeSlots,
  showToast 
}) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [configuredSlots, setConfiguredSlots] = useState([]);
  const [customRanges, setCustomRanges] = useState([]);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [editingRange, setEditingRange] = useState(null);
  const [showAddRange, setShowAddRange] = useState(false);
  
  // Weekly schedule state
  const [weeklySchedule, setWeeklySchedule] = useState({});
  const [loadingWeekly, setLoadingWeekly] = useState(false);
  const [savingWeekly, setSavingWeekly] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [dayInputs, setDayInputs] = useState({});
  const [hasOverride, setHasOverride] = useState(false);

  // New range inputs (24-hour format)
  const [newRangeStart, setNewRangeStart] = useState('09:00');
  const [newRangeEnd, setNewRangeEnd] = useState('17:00');
  const [newRangeInterval, setNewRangeInterval] = useState('30');

  // Load weekly schedule on mount
  useEffect(() => {
    loadWeeklySchedule();
  }, []);

  // Load slots when date changes
  useEffect(() => {
    if (selectedDate) {
      loadConfiguredSlots();
      checkDateOverride();
    }
  }, [selectedDate]);

  const loadWeeklySchedule = async () => {
    setLoadingWeekly(true);
    try {
      const schedule = await getWeeklySchedule();
      setWeeklySchedule(schedule || {});
    } catch (error) {
      console.error('Error loading weekly schedule:', error);
      showToast('Failed to load weekly schedule', 'error');
    } finally {
      setLoadingWeekly(false);
    }
  };

  const checkDateOverride = async () => {
    if (!selectedDate) return;
    try {
      const override = await hasDateOverride(selectedDate);
      setHasOverride(override);
    } catch (error) {
      console.error('Error checking override:', error);
    }
  };

  const loadConfiguredSlots = async () => {
    if (!selectedDate) return;
    
    setLoadingSlots(true);
    try {
      const slots = await getConfiguredSlots(selectedDate);
      const sortedSlots = sortTimeSlots(slots);
      setConfiguredSlots(sortedSlots);
      
      // Try to convert slots back to ranges (for display)
      // For now, we'll just show them as individual slots until we have range data
      setCustomRanges([]);
    } catch (error) {
      console.error('Error loading slots:', error);
      showToast('Failed to load availability', 'error');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setShowAddRange(false);
    setEditingRange(null);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    setSelectedDate(today);
  };

  // Generate calendar days for current month
  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());
    
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));
    
    const days = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  // Format time for display (24-hour format, already in correct format)
  const formatTimeForDisplay = (timeStr) => {
    // If it's already in 24-hour format (HH:MM), return as is
    if (/^\d{2}:\d{2}$/.test(timeStr)) {
      return timeStr;
    }
    // If it's in old 12-hour format, convert it (for backward compatibility)
    const [time, period] = timeStr.split(' ');
    if (!period) return timeStr; // Already 24-hour
    const [hours, minutes] = time.split(':').map(Number);
    let hour24 = hours;
    if (period === 'PM' && hours !== 12) hour24 = hours + 12;
    if (period === 'AM' && hours === 12) hour24 = 0;
    return `${hour24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Weekly Schedule Functions
  const getDayRanges = (dayId) => {
    return weeklySchedule[dayId]?.ranges || [];
  };

  const updateDayInput = (dayId, field, value) => {
    setDayInputs(prev => ({
      ...prev,
      [dayId]: {
        ...prev[dayId],
        [field]: value
      }
    }));
  };

  const getDayInput = (dayId, field, defaultValue = '') => {
    return dayInputs[dayId]?.[field] || defaultValue;
  };

  const addDayRange = (dayId, startTime, endTime, interval) => {
    // Validate 24-hour format (HH:MM)
    if (!/^\d{2}:\d{2}$/.test(startTime) || !/^\d{2}:\d{2}$/.test(endTime)) {
      showToast('Please enter time in 24-hour format (HH:MM)', 'error');
      return;
    }
    
    const currentRanges = getDayRanges(dayId);
    const newRange = { start: startTime, end: endTime, interval: parseInt(interval) || 30 };
    
    const updatedSchedule = {
      ...weeklySchedule,
      [dayId]: {
        ranges: [...currentRanges, newRange]
      }
    };
    
    setWeeklySchedule(updatedSchedule);
    updateDayInput(dayId, 'startTime', '09:00');
    updateDayInput(dayId, 'endTime', '17:00');
    updateDayInput(dayId, 'interval', '30');
    setEditingDay(null);
  };

  const removeDayRange = (dayId, rangeIndex) => {
    const currentRanges = getDayRanges(dayId);
    const updatedSchedule = {
      ...weeklySchedule,
      [dayId]: {
        ranges: currentRanges.filter((_, idx) => idx !== rangeIndex)
      }
    };
    
    setWeeklySchedule(updatedSchedule);
  };

  const saveWeeklySchedule = async () => {
    setSavingWeekly(true);
    try {
      await setWeeklySchedule(weeklySchedule);
      showToast('Weekly schedule saved successfully', 'success');
    } catch (error) {
      console.error('Error saving weekly schedule:', error);
      showToast('Failed to save weekly schedule', 'error');
    } finally {
      setSavingWeekly(false);
    }
  };

  // Date-specific override functions
  const addCustomRange = () => {
    // Validate 24-hour format (HH:MM)
    if (!/^\d{2}:\d{2}$/.test(newRangeStart) || !/^\d{2}:\d{2}$/.test(newRangeEnd)) {
      showToast('Please enter time in 24-hour format (HH:MM)', 'error');
      return;
    }
    
    const interval = parseInt(newRangeInterval) || 30;
    const newRange = { start: newRangeStart, end: newRangeEnd, interval };
    setCustomRanges([...customRanges, newRange]);
    
    // Reset inputs
    setNewRangeStart('09:00');
    setNewRangeEnd('17:00');
    setNewRangeInterval('30');
    setShowAddRange(false);
  };

  const removeRange = (index) => {
    setCustomRanges(customRanges.filter((_, idx) => idx !== index));
  };

  const saveAvailability = async () => {
    if (!selectedDate) return;
    
    setSavingAvailability(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      
      // Convert ranges to slots
      const slots = convertRangesToSlots(customRanges);
      
      await setAvailability(dateStr, slots);
      showToast('Availability override saved successfully', 'success');
      setConfiguredSlots(slots);
      setHasOverride(true);
      await checkDateOverride();
    } catch (error) {
      console.error('Error saving availability:', error);
      showToast('Failed to save availability', 'error');
    } finally {
      setSavingAvailability(false);
    }
  };

  const clearDateOverride = async () => {
    if (!selectedDate) return;
    
    setSavingAvailability(true);
    try {
      await deleteAvailabilityOverride(selectedDate);
      showToast('Override cleared - using weekly schedule', 'success');
      await loadConfiguredSlots();
      setHasOverride(false);
      setCustomRanges([]);
      await checkDateOverride();
    } catch (error) {
      console.error('Error clearing override:', error);
      showToast('Failed to clear override', 'error');
    } finally {
      setSavingAvailability(false);
    }
  };

  const calendarDays = getCalendarDays();
  const today = new Date();
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentMonth.getMonth() && 
           date.getFullYear() === currentMonth.getFullYear();
  };

  // Generate preview slots from ranges
  const getPreviewSlots = (ranges) => {
    return convertRangesToSlots(ranges);
  };

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-[#c5a059] to-[#b08e4d] rounded-xl shadow-lg">
            <Calendar className="text-white" size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Manage Availability</h2>
            <p className="text-sm text-gray-500 mt-1">Set time ranges for weekly schedule & date overrides</p>
          </div>
        </div>
        <HelpTooltip
          title="Availability Management"
          content="Set time ranges (e.g., 9:00 AM - 5:00 PM) instead of individual times. The system will automatically generate booking slots at your specified interval (e.g., every 30 minutes)."
        />
      </div>

      <FeatureGuide
        title="Time Range Availability"
        description="Set time ranges instead of individual times! For example, '9:00 AM - 5:00 PM' with 30-minute intervals will automatically create slots at 9:00 AM, 9:30 AM, 10:00 AM, and so on."
        steps={[
          "Set your weekly schedule below - add time ranges for each day",
          "Specify start time, end time, and interval (e.g., 30 minutes)",
          "Click 'Save Weekly Schedule' to apply",
          "Use the calendar to override specific dates with custom ranges",
          "Click 'Clear Override' to revert to weekly schedule"
        ]}
        tips={[
          "Time ranges automatically generate booking slots",
          "Common intervals: 15, 30, 45, or 60 minutes",
          "You can add multiple ranges per day (e.g., morning and afternoon)",
          "Date overrides take priority over weekly schedule",
          "Empty day = no availability (closed)"
        ]}
      />

      <InfoBanner type="info" title="How Time Ranges Work">
        <ul className="list-disc list-inside space-y-1 text-sm">
          <li><strong>Time Ranges:</strong> Set a start and end time (e.g., 9:00 AM - 5:00 PM) with an interval (e.g., 30 minutes).</li>
          <li><strong>Auto-Generated Slots:</strong> The system creates booking slots at your specified interval within each range.</li>
          <li><strong>Multiple Ranges:</strong> Add multiple ranges per day for split schedules (e.g., 9:00 AM - 12:00 PM and 2:00 PM - 5:00 PM).</li>
        </ul>
      </InfoBanner>

      {/* Weekly Schedule Section */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-md">
              <Sparkles className="text-white" size={20} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Weekly Schedule</h3>
              <p className="text-sm text-gray-500 mt-1">Set time ranges for each day of the week</p>
            </div>
          </div>
          <button
            onClick={saveWeeklySchedule}
            disabled={savingWeekly || loadingWeekly}
            className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#c5a059] to-[#b08e4d] hover:from-[#b08e4d] hover:to-[#9d7d42] text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold min-h-[48px]"
          >
            {savingWeekly ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} className="mr-2" />
                Save Weekly Schedule
              </>
            )}
          </button>
        </div>

        {loadingWeekly ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={32} className="animate-spin text-[#c5a059]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {DAYS_OF_WEEK.map((day) => {
              const dayRanges = getDayRanges(day.id);
              const isEditing = editingDay === day.id;
              const startTime = getDayInput(day.id, 'startTime', '09:00');
              const endTime = getDayInput(day.id, 'endTime', '17:00');
              const interval = getDayInput(day.id, 'interval', '30');

              return (
                <div 
                  key={day.id} 
                  className="border-2 border-gray-200 rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white hover:border-[#c5a059] transition-all duration-200 hover:shadow-md"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-bold text-gray-900 text-lg">{day.label}</h4>
                    {!isEditing && (
                      <button
                        onClick={() => {
                          setEditingDay(day.id);
                          updateDayInput(day.id, 'startTime', '09:00');
                          updateDayInput(day.id, 'endTime', '17:00');
                          updateDayInput(day.id, 'interval', '30');
                        }}
                        className="p-2 text-[#c5a059] hover:bg-[#c5a059]/10 rounded-lg transition-colors"
                        aria-label={`Add time range for ${day.label}`}
                      >
                        <Plus size={18} />
                      </button>
                    )}
                  </div>

                  {isEditing && (
                    <div className="mb-4 p-4 bg-white rounded-lg border-2 border-[#c5a059] shadow-sm space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col min-w-0">
                          <label className="text-xs font-medium text-gray-600 mb-1.5">Start (24h)</label>
                          <input
                            type="text"
                            placeholder="09:00"
                            value={startTime}
                            onChange={(e) => updateDayInput(day.id, 'startTime', e.target.value)}
                            className="w-full px-3 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c5a059] text-sm"
                            pattern="\d{2}:\d{2}"
                            maxLength="5"
                          />
                        </div>
                        <div className="flex flex-col min-w-0">
                          <label className="text-xs font-medium text-gray-600 mb-1.5">End (24h)</label>
                          <input
                            type="text"
                            placeholder="17:00"
                            value={endTime}
                            onChange={(e) => updateDayInput(day.id, 'endTime', e.target.value)}
                            className="w-full px-3 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c5a059] text-sm"
                            pattern="\d{2}:\d{2}"
                            maxLength="5"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <label className="text-xs font-medium text-gray-600 mb-1.5">Interval (minutes)</label>
                        <select
                          value={interval}
                          onChange={(e) => updateDayInput(day.id, 'interval', e.target.value)}
                          className="w-full px-3 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c5a059] text-sm bg-white"
                        >
                          <option value="15">15 minutes</option>
                          <option value="30">30 minutes</option>
                          <option value="45">45 minutes</option>
                          <option value="60">60 minutes</option>
                        </select>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={() => addDayRange(day.id, startTime, endTime, interval)}
                          className="flex-1 px-3 h-10 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition text-sm font-medium"
                        >
                          Add
                        </button>
                        <button
                          onClick={() => {
                            setEditingDay(null);
                          }}
                          className="px-3 h-10 bg-gray-200 hover:bg-gray-300 rounded-lg transition text-sm font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {dayRanges.length === 0 ? (
                    <div className="text-center py-6">
                      <Clock size={24} className="mx-auto text-gray-300 mb-2" />
                      <p className="text-xs text-gray-400 font-medium">No time ranges set</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {dayRanges.map((range, idx) => {
                        const previewSlots = generateSlotsFromRange(range.start, range.end, range.interval || 30);
                        return (
                          <div
                            key={idx}
                            className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Timer size={14} className="text-[#c5a059]" />
                                <span className="text-sm font-semibold text-gray-800">
                                  {range.start} - {range.end}
                                </span>
                              </div>
                              <button
                                onClick={() => removeDayRange(day.id, idx)}
                                className="text-gray-400 hover:text-red-600 transition-colors p-1"
                                aria-label={`Remove range`}
                              >
                                <X size={14} />
                              </button>
                            </div>
                            <p className="text-xs text-gray-500">
                              {previewSlots.length} slots ({range.interval || 30} min intervals)
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Calendar View for Overrides */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg shadow-md">
              <Calendar className="text-white" size={20} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Date-Specific Overrides</h3>
              <p className="text-sm text-gray-500">Select dates to override the weekly schedule</p>
            </div>
          </div>
        </div>

        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigateMonth(-1)}
            className="p-3 hover:bg-gray-100 rounded-xl transition min-h-[48px] min-w-[48px] flex items-center justify-center shadow-sm hover:shadow"
            aria-label="Previous month"
          >
            <ChevronLeft size={20} className="text-gray-600" />
          </button>
          
          <div className="flex items-center gap-4">
            <h4 className="text-lg font-bold text-gray-800">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h4>
            <button
              onClick={goToToday}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl transition text-sm font-medium shadow-sm hover:shadow min-h-[44px]"
            >
              Today
            </button>
          </div>
          
          <button
            onClick={() => navigateMonth(1)}
            className="p-3 hover:bg-gray-100 rounded-xl transition min-h-[48px] min-w-[48px] flex items-center justify-center shadow-sm hover:shadow"
            aria-label="Next month"
          >
            <ChevronRight size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2 mb-4">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-sm font-bold text-gray-600 py-2">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-2">
          {calendarDays.map((date, idx) => {
            const dateStr = date.toISOString().split('T')[0];
            const isSelected = selectedDate && selectedDate.toISOString().split('T')[0] === dateStr;
            const isTodayDate = isToday(date);
            const isPast = date < today && !isTodayDate;
            const inCurrentMonth = isCurrentMonth(date);
            
            return (
              <button
                key={idx}
                onClick={() => !isPast && handleDateSelect(date)}
                disabled={isPast}
                className={`
                  aspect-square p-2 rounded-xl border-2 transition-all relative
                  min-h-[48px] md:min-h-[64px]
                  ${isSelected 
                    ? 'border-[#c5a059] bg-gradient-to-br from-[#fdfbf7] to-[#f9f5f0] ring-2 ring-[#c5a059] ring-opacity-30 shadow-md scale-105' 
                    : 'border-gray-200 hover:border-[#c5a059] hover:bg-gray-50 hover:shadow-sm'
                  }
                  ${isTodayDate ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-300' : ''}
                  ${!inCurrentMonth ? 'opacity-40' : ''}
                  ${isPast ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer active:scale-95'}
                `}
              >
                {isTodayDate && (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-[#c5a059] rounded-full shadow-sm"></span>
                )}
                <div className={`text-sm font-bold ${
                  isSelected ? 'text-[#c5a059]' : isTodayDate ? 'text-blue-700' : 'text-gray-800'
                }`}>
                  {date.getDate()}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Time Ranges Editor for Selected Date */}
      {selectedDate && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                {formatDisplayDate(selectedDate, { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </h3>
              <p className="text-sm text-gray-500 flex items-center gap-1.5">
                {hasOverride ? (
                  <>
                    <CheckCircle2 size={14} className="text-amber-600" />
                    <span className="text-amber-600 font-medium">Custom override active</span>
                  </>
                ) : (
                  <span>Using weekly schedule</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {hasOverride && (
                <button
                  onClick={clearDateOverride}
                  className="flex items-center px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl transition font-medium text-sm shadow-sm hover:shadow min-h-[44px]"
                >
                  <RotateCcw size={16} className="mr-2" />
                  Clear Override
                </button>
              )}
              <button
                onClick={() => {
                  setShowAddRange(!showAddRange);
                  setEditingRange(null);
                }}
                className="flex items-center px-4 py-2.5 bg-gradient-to-r from-[#c5a059] to-[#b08e4d] hover:from-[#b08e4d] hover:to-[#9d7d42] text-white rounded-xl transition font-medium text-sm shadow-md hover:shadow-lg min-h-[44px]"
              >
                <Plus size={18} className="mr-2" />
                Add Time Range
              </button>
            </div>
          </div>

          {loadingSlots ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={32} className="animate-spin text-[#c5a059]" />
            </div>
          ) : (
            <>
              {/* Add New Range Form */}
              {showAddRange && (
                <div className="mb-6 p-5 bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-[#c5a059] shadow-md">
                  <h4 className="font-semibold text-gray-800 mb-4">Add Time Range</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col min-w-0">
                      <label className="text-sm font-medium text-gray-700 mb-1.5">Start Time (24h)</label>
                      <input
                        type="text"
                        placeholder="09:00"
                        value={newRangeStart}
                        onChange={(e) => setNewRangeStart(e.target.value)}
                        className="w-full px-4 h-11 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c5a059] text-sm"
                        pattern="\d{2}:\d{2}"
                        maxLength="5"
                      />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <label className="text-sm font-medium text-gray-700 mb-1.5">End Time (24h)</label>
                      <input
                        type="text"
                        placeholder="17:00"
                        value={newRangeEnd}
                        onChange={(e) => setNewRangeEnd(e.target.value)}
                        className="w-full px-4 h-11 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c5a059] text-sm"
                        pattern="\d{2}:\d{2}"
                        maxLength="5"
                      />
                    </div>
                  </div>
                  <div className="mb-4 flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1.5">Interval (minutes)</label>
                    <select
                      value={newRangeInterval}
                      onChange={(e) => setNewRangeInterval(e.target.value)}
                      className="w-full px-4 h-11 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c5a059] text-sm bg-white"
                    >
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">60 minutes</option>
                    </select>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={addCustomRange}
                      className="flex-1 px-4 h-11 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition font-medium text-sm shadow-sm hover:shadow"
                    >
                      Add Range
                    </button>
                    <button
                      onClick={() => {
                        setShowAddRange(false);
                      }}
                      className="px-4 h-11 bg-gray-200 hover:bg-gray-300 rounded-lg transition font-medium text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Time Ranges List */}
              {customRanges.length === 0 ? (
                <div className="text-center py-12">
                  <Clock size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 font-medium mb-1">No time ranges configured</p>
                  <p className="text-sm text-gray-400">Add your first time range above or leave empty to use weekly schedule</p>
                </div>
              ) : (
                <div className="space-y-3 mb-6">
                  {customRanges.map((range, idx) => {
                    const previewSlots = generateSlotsFromRange(range.start, range.end, range.interval || 30);
                    return (
                      <div
                        key={idx}
                        className="p-4 bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-gray-200 shadow-sm hover:shadow-md transition-all"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-[#c5a059]/10 rounded-lg">
                              <Timer size={18} className="text-[#c5a059]" />
                            </div>
                            <div>
                              <span className="font-bold text-gray-900 text-lg">
                                {range.start} - {range.end}
                              </span>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {previewSlots.length} booking slots • {range.interval || 30} min intervals
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => removeRange(idx)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition min-h-[44px] min-w-[44px] flex items-center justify-center"
                            title="Remove range"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Save Button */}
              <div className="flex items-center justify-end">
                <button
                  onClick={saveAvailability}
                  disabled={savingAvailability || customRanges.length === 0}
                  className="flex items-center px-6 py-3 bg-gradient-to-r from-[#c5a059] to-[#b08e4d] hover:from-[#b08e4d] hover:to-[#9d7d42] text-white rounded-xl shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all font-semibold min-h-[48px]"
                >
                  {savingAvailability ? (
                    <>
                      <Loader2 size={20} className="mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={20} className="mr-2" />
                      Save Override
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
